<template>
  <div class="bible-embedded card shadow-sm mb-3 overflow-hidden" :class="{ compact: compact }" v-if="references.length">
    <div class="card-header bg-primary text-white py-2 d-flex align-items-center">
      <span class="material-icons small me-2">menu_book</span>
      <span class="fw-bold small">{{ $t("message.bible") }}</span>
    </div>
    
    <div class="card-body p-2">
      <div class="bible-chips d-flex flex-wrap gap-1 mb-2">
        <button 
          v-for="ref in references" 
          :key="ref" 
          class="btn btn-sm py-1 px-3 rounded-pill transition-all"
          :class="selectedReference === ref ? 'btn-primary' : 'btn-outline-primary bg-light text-primary border-0'"
          @click="selectReference(ref)"
        >
          {{ ref }}
        </button>
      </div>

      <div v-if="loading" class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      </div>

      <div v-else-if="selectedData && selectedData.text" class="bible-preview p-3 bg-light rounded-3 border-start border-primary border-4 text-start">
        <p class="mb-2 fs-6 text-dark lh-base">{{ selectedData.text }}</p>
        <div class="d-flex justify-content-between align-items-center mt-2">
          <small class="text-muted fw-bold">{{ selectedData.translation }}</small>
          <button class="btn btn-link btn-sm p-0 text-decoration-none" @click="openChapter">
            {{ $t("message.readFullChapter") }} 
            <span class="material-icons tiny align-middle">open_in_new</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  extractBibleReferences,
  fetchBiblePassage,
  parseBibleReference,
} from "../utils/bibleReferences.js";
import { UserStore } from "../UserStore.js";

export default {
  name: "BibleEmbeddedView",
  props: {
    content: {
      type: String,
      default: "",
    },
    compact: {
      type: Boolean,
      default: false,
    },
    openChapterOnPress: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedReference: "",
      selectedData: null,
      loading: false,
    };
  },
  computed: {
    references() {
      return extractBibleReferences(this.content);
    },
  },
  watch: {
    references: {
      immediate: true,
      handler(newRefs) {
        if (newRefs.length > 0 && !this.selectedReference) {
          this.selectReference(newRefs[0], false);
        }
      },
    },
  },
  methods: {
    async selectReference(reference, isUserAction = true) {
      if (this.openChapterOnPress && isUserAction) {
          this.selectedReference = reference;
          this.openChapter();
          return;
      }

      this.selectedReference = reference;
      if (this.openChapterOnPress && !isUserAction) return;

      this.loading = true;
      try {
        const locale = this.$i18n ? this.$i18n.locale : "en";
        const me = await UserStore.getMe();
        const customTranslation = (me && me.data && me.data.bibleTranslation) || "";
        this.selectedData = await fetchBiblePassage(reference, locale, customTranslation);
      } catch (error) {
        console.error("BibleEmbeddedView Error:", error);
        this.selectedData = null;
      } finally {
        this.loading = false;
      }
    },
    openChapter() {
      const parsed = parseBibleReference(this.selectedReference);
      this.$router.push({
        name: "BibleChapter",
        params: { reference: parsed.chapterReference, verse: parsed.verse },
      });
    },
  },
};
</script>

<style scoped>
.bible-embedded {
  border: none;
}

.transition-all {
  transition: all 0.2s ease-in-out;
}

.tiny {
  font-size: 14px;
}

.bible-preview {
  background-color: #f8fafc !important;
}

.bible-chips button {
  font-weight: 600;
  font-size: 0.8rem;
}

.compact .bible-preview p {
  font-size: 0.85rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
